import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import {
  ATTRIBUTES_API,
  ATTRIBUTE_VALUES_API,
  fetchAttributeValuesByAttributeId,
  normalizeAttribute,
  normalizeAttributeValue
} from "../../shared/catalogMastersApi";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  name: "",
  slug: "",
  isFilterable: false,
  isVarient: false
});

const emptyValueForm = () => ({
  value: "",
  colorCode: "",
  order: 0
});

function mapValueDtoToForm(item) {
  return {
    value: item.value ?? "",
    colorCode: item.colorCode ?? "",
    order: item.order ?? 0
  };
}

function pickPrimaryValue(list) {
  if (!list?.length) return null;
  const sorted = [...list].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return sorted[0];
}

function valueFormHasIntent(vf, valueEditingId) {
  if (valueEditingId) return true;
  return Boolean(vf.value.trim() || vf.colorCode?.trim() || Number(vf.order) !== 0);
}

export function AttributesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const editParam = searchParams.get("edit");
  const valueEditParam = searchParams.get("valueEdit");
  const skipValueHydrateRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [valueError, setValueError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(() => emptyForm());

  const [valueEditingId, setValueEditingId] = useState(null);
  const [valueForm, setValueForm] = useState(() => emptyValueForm());

  const activeTab = searchParams.get("tab") === "values" ? "values" : "attribute";

  useEffect(() => {
    if (!editParam && searchParams.get("valueEdit")) {
      const next = new URLSearchParams(searchParams);
      next.delete("valueEdit");
      setSearchParams(next, { replace: true });
    }
  }, [editParam, searchParams, setSearchParams]);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      setValueEditingId(null);
      setValueForm(emptyValueForm());
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      const res = await apiGet(`${ATTRIBUTES_API}/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load attribute");
        return;
      }
      const x = normalizeAttribute(res.data);
      if (!x) return;
      setEditingId(x.id);
      setForm({
        name: x.name ?? "",
        slug: x.slug ?? "",
        isFilterable: x.isFilterable,
        isVarient: x.isVarient
      });
      const keepValueDraft = new URLSearchParams(window.location.search).get("valueEdit");
      if (!keepValueDraft) {
        skipValueHydrateRef.current = false;
        setValueEditingId(null);
        setValueForm(emptyValueForm());
      }
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  useEffect(() => {
    if (!valueEditParam || activeTab !== "values" || !editingId) {
      return;
    }
    const id = Number(valueEditParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    (async () => {
      const res = await apiGet(`${ATTRIBUTE_VALUES_API}/${id}`);
      if (cancelled) return;
      if (!res.ok) {
        setValueError(res.error ?? "Failed to load attribute value");
        return;
      }
      const row = normalizeAttributeValue(res.data);
      if (!row) return;
      if (Number(row.attributeId) !== Number(editingId)) {
        setValueError("This value does not belong to the selected attribute.");
        return;
      }
      setValueError(null);
      setValueEditingId(row.id);
      setValueForm(mapValueDtoToForm(row));
    })();
    return () => {
      cancelled = true;
    };
  }, [valueEditParam, activeTab, editingId]);

  useEffect(() => {
    if (!editingId || activeTab !== "values" || valueEditParam) {
      return;
    }
    if (skipValueHydrateRef.current) {
      skipValueHydrateRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const { ok, items, error: err } = await fetchAttributeValuesByAttributeId(editingId);
      if (cancelled) return;
      if (!ok) {
        setValueError(err ?? "Failed to load attribute values");
        return;
      }
      setValueError(null);
      const pick = pickPrimaryValue(items);
      if (!pick) {
        setValueEditingId(null);
        setValueForm(emptyValueForm());
        return;
      }
      setValueEditingId(pick.id);
      setValueForm(mapValueDtoToForm(pick));
    })();
    return () => {
      cancelled = true;
    };
  }, [editingId, activeTab, valueEditParam]);

  const selectTab = (tab) => {
    if (tab === "attribute") {
      const next = new URLSearchParams(searchParams);
      next.delete("tab");
      next.delete("valueEdit");
      setSearchParams(next, { replace: true });
      setValueError(null);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("tab", "values");
    next.delete("valueEdit");
    setSearchParams(next, { replace: true });
    setValueError(null);
  };

  const saveAll = async () => {
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }

    const vIntent = valueFormHasIntent(valueForm, valueEditingId);
    if (vIntent && !valueForm.value.trim()) {
      setError(null);
      setValueError("Value is required—or clear the value fields.");
      return;
    }

    setSaving(true);
    setError(null);
    setValueError(null);

    const priorAttrId = editingId;
    const attrRes = await apiPost(ATTRIBUTES_API, {
      id: priorAttrId ?? 0,
      name: form.name,
      slug: form.slug || null,
      isFilterable: form.isFilterable,
      isVarient: form.isVarient
    });

    if (!attrRes.ok) {
      setError(attrRes.error ?? "Failed to save attribute");
      setSaving(false);
      return;
    }

    const attributeId = priorAttrId ?? attrRes.data?.id;
    if (!attributeId) {
      setError("Attribute was saved but no id was returned.");
      setSaving(false);
      return;
    }

    if (!priorAttrId && attributeId) {
      setEditingId(attributeId);
    }

    if (vIntent) {
      const valRes = await apiPost(ATTRIBUTE_VALUES_API, {
        id: valueEditingId ?? 0,
        attributeId,
        value: valueForm.value || null,
        colorCode: valueForm.colorCode || null,
        order: Number(valueForm.order || 0)
      });
      if (!valRes.ok) {
        setValueError(valRes.error ?? "Failed to save attribute value");
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    navigate("/attributes-list");
  };

  const onRefreshAttribute = async () => {
    setError(null);
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`${ATTRIBUTES_API}/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load attribute");
        return;
      }
      const x = normalizeAttribute(res.data);
      if (!x) return;
      setEditingId(x.id);
      setForm({
        name: x.name ?? "",
        slug: x.slug ?? "",
        isFilterable: x.isFilterable,
        isVarient: x.isVarient
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  const onRefreshClick = async () => {
    if (activeTab === "values") {
      setValueError(null);
      if (!editingId) return;
      const vid = searchParams.get("valueEdit");
      if (vid && editingId) {
        const res = await apiGet(`${ATTRIBUTE_VALUES_API}/${Number(vid)}`);
        if (res.ok) {
          const row = normalizeAttributeValue(res.data);
          if (row && Number(row.attributeId) === Number(editingId)) {
            setValueEditingId(row.id);
            setValueForm(mapValueDtoToForm(row));
          }
        }
      } else {
        const { ok, items, error: err } = await fetchAttributeValuesByAttributeId(editingId);
        if (!ok) {
          setValueError(err ?? "Failed to load attribute values");
          return;
        }
        const pick = pickPrimaryValue(items);
        if (!pick) {
          setValueEditingId(null);
          setValueForm(emptyValueForm());
        } else {
          setValueEditingId(pick.id);
          setValueForm(mapValueDtoToForm(pick));
        }
      }
    } else {
      await onRefreshAttribute();
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Attribute" : "Add Attribute"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void saveAll()}
            disabled={saving || loading}
            title="Save attribute and value"
            aria-label="Save"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefreshClick()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/attributes-list" className="icon-button icon-button--toolbar" title="Attributes list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>

      <div className="card-body">
        <div className="form-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "attribute" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "attribute"}
            onClick={() => selectTab("attribute")}
          >
            Attribute
          </button>
          <button
            type="button"
            role="tab"
            className={`form-tab ${activeTab === "values" ? "form-tab--active" : ""}`}
            aria-selected={activeTab === "values"}
            onClick={() => selectTab("values")}
          >
            Attribute values
          </button>
        </div>

        {activeTab === "attribute" && (
          <form
            id="attributes-form"
            className="form form-fields-bottomline"
            onSubmit={(e) => {
              e.preventDefault();
              void saveAll();
            }}
          >
            <div className="form-row form-row--4">
              <label className="field">
                <span>Name</span>
                <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
              </label>
              <label className="field">
                <span>Slug</span>
                <input type="text" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} />
              </label>
            </div>
            {error && <div className="error-banner">{error}</div>}
            {valueError && activeTab === "attribute" && <div className="error-banner">{valueError}</div>}
          </form>
        )}

        {activeTab === "values" && (
          <div className="form-tab-panel">
            {error && <div className="error-banner">{error}</div>}
            {valueError && <div className="error-banner">{valueError}</div>}

            <form
              id="attribute-values-inline-form"
              className="form form-fields-bottomline"
              onSubmit={(e) => {
                e.preventDefault();
                void saveAll();
              }}
            >
              <div className="form-row form-row--4">
                <label className="field">
                  <span>Value</span>
                  <input type="text" value={valueForm.value} onChange={(e) => setValueForm((p) => ({ ...p, value: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Color Code</span>
                  <input type="text" value={valueForm.colorCode} onChange={(e) => setValueForm((p) => ({ ...p, colorCode: e.target.value }))} />
                </label>
                <label className="field">
                  <span>Order</span>
                  <input type="number" value={valueForm.order} onChange={(e) => setValueForm((p) => ({ ...p, order: e.target.value }))} />
                </label>
              </div>
              {(valueEditingId || searchParams.get("valueEdit")) && (
                <div className="form-actions">
                  <button
                    className="btn btn-ghost"
                    type="button"
                    onClick={() => {
                      skipValueHydrateRef.current = true;
                      setValueEditingId(null);
                      setValueForm(emptyValueForm());
                      const next = new URLSearchParams(searchParams);
                      next.delete("valueEdit");
                      setSearchParams(next, { replace: true });
                    }}
                  >
                    New attribute value
                  </button>
                </div>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
